import "./toggle-button.css";

import { Util } from "../../../utils/util";
import { IToggleButtonData, IToggleButtonOptionData } from "./toggle-button.interface";

/**
 * Represents a toggle button.
 */
export class ToggleButton {
    private readonly _item: IToggleButtonData;
    private readonly _containerElement: HTMLElement;
    private _toggleOffOptionElement: HTMLElement | null = null;
    private _toggleOnOptionElement: HTMLElement | null = null;

    constructor(item: IToggleButtonData) {
        this._item = item;
        this._containerElement = this._createElement();
        this._updateToggleOption();
        this._setLockedState(this._item.locked);
    }

    public get element(): HTMLElement {
        return this._containerElement;
    }

    private _createElement(): HTMLElement {

        this._toggleOffOptionElement = this._createOptionElement(this._item.toggleOffOption, this._item.toggleName + "_off");
        this._toggleOnOptionElement = this._createOptionElement(this._item.toggleOnOption, this._item.toggleName + "_on");

        const container = document.createElement("div");
        container.classList.add("toggle-control");
        container.tabIndex = 0;
        container.id = `div_${this._item.toggleName}`;
        container.appendChild(this._toggleOffOptionElement);
        container.appendChild(this._toggleOnOptionElement);

        container.onmousedown = (event) => {
            event.preventDefault();
        }

        container.addEventListener("click", () => {
            if (this._item.locked) return;
            this._onToggle();
        });
        Util.addKeyboardActivation(container, () => {
            if (!this._item.locked)
                this._onToggle();
        });

        container.addEventListener("focus", () => {
            this._handleFocusAnnouncement();
        });

        return container;
    }

    private _createOptionElement(optionData: IToggleButtonOptionData, id: string): HTMLLIElement {
        const optionHtml = `
            <div
                role="option"
                id="${id}"
                ${optionData.ltag ? `ltag="${optionData.ltag}"` : ""}
                ${optionData.ltagj ? `ltagj="${optionData.ltagj}"` : ""}
                aria-hidden="true"
            ><span class="icon-lock"></span> <span>${optionData.label}</span></div>`;
        const element = Util.createHtmlElementFromTemplate(optionHtml) as HTMLLIElement;

        return element;
    }

    private _onToggle(): void {
        this._item.isToggled = !this._item.isToggled;
        this._updateToggleOption();
        this._executeToggleAction();
    }

    private _updateToggleOption(): void {
        if (this._item.isToggled) {
            this._activateToggleOnOption();
        } else {
            this._activateToggleOffOption();
        }
    }

    private _activateToggleOffOption(): void {
        if (this._toggleOffOptionElement) {
            this._toggleOffOptionElement.classList.add("active");
            this._toggleOffOptionElement.ariaSelected = "true";
        }
        if (this._toggleOnOptionElement) {
            this._toggleOnOptionElement.classList.remove("active");
            this._toggleOnOptionElement.ariaSelected = "false";
        }
    }

    private _activateToggleOnOption(): void {
        if (this._toggleOnOptionElement) {
            this._toggleOnOptionElement.classList.add("active");
            this._toggleOnOptionElement.ariaSelected = "true";
        }
        if (this._toggleOffOptionElement) {
            this._toggleOffOptionElement.classList.remove("active");
            this._toggleOffOptionElement.ariaSelected = "false";
        }
    }

    private _executeToggleAction() {
        if (this._item.isToggled) {
            this._item.toggleOnOption.function();
            Util.announce(this._item.toggleOnOption.announcementOnSelect ?? "");
        } else {
            this._item.toggleOffOption.function();
            Util.announce(this._item.toggleOffOption.announcementOnSelect ?? "");
        }
    }

    private _setLockedState(isLocked: boolean): void {
        this._item.locked = isLocked;

        if (isLocked) {
            this.element.classList.add("locked");
            this.element.setAttribute("aria-disabled", "true");
            this.element.setAttribute("aria-hidden", "true");
        } else {
            this.element.classList.remove("locked");
            this.element.removeAttribute("aria-disabled");
            this.element.removeAttribute("aria-hidden");
        }
    }

    private _handleFocusAnnouncement(): void {
        if (this._item.locked) {
            Util.announce(this._item.announcementOnFocusLocked ?? "");
        } else {
            Util.announce(this._item.announcementOnFocus ?? "");
        }

        if (this._item.isToggled) {
            Util.announce(this._item.toggleOnOption.announcementOnSelect ?? "");
        } else {
            Util.announce(this._item.toggleOffOption.announcementOnSelect ?? "");
        }
    }
}
